// Onboarding.jsx
import React, { useEffect } from "react";
import styled from "styled-components";
import { Typography, Divider } from "antd";
import PersonalInfoSection from "./sections/PersonalInfoSection";
import AssistantInfoSection from "./sections/AssistantInfoSection";
import LicenseSection from "./sections/LicenseSection";
import BusinessInfoSection from "./sections/BusinessInfoSection";
import BrandingSection from "./sections/BrandingSection";
import AssociatesSection from "./sections/AssociatesSection";
import ReferralsSection from "./sections/ReferralsSection";
import container from "container/BrokerDashboard/editAgentContainer";
import {
  getOnboardingAgentLicenseStateAction,
  getOnboardingAssociationInfoAction,
} from "container/BrokerDashboard/actions";
import DocumentDetails from "./sections/DocumentDetails";

const { Title } = Typography;

// Function to extract agent ID from URL
const getAgentIdFromUrl = () => {
  const pathname = window.location.pathname;
  const match = pathname.match(/\/broker\/dashboard\/(\d+)\/orientation-info/);
  return match ? parseInt(match[1]) : null; // return integer if found, null if not found
};

const PageContainer = styled.div`
  padding: 32px;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
  }

  button {
    font-size: 14px;
  }
`;

const Section = styled.section`
  margin-bottom: 40px;
  padding: 24px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
`;

const Onboarding = ({
  getOnboardingPersonalInfo,
  getOnboardingReferralInfo,
  getOnboardingLicenseInfo,
  getOnboardingAssociationInfo,
  getOnboardingAssociateInviteInfo,
  getOnboardingOfficeInfo,
  getOnboardingDbaInfo,
  getOnboardingAgentLicenseStateInfo,
  getOnboardingPersonalInfoResponse,
  getOnboardingLicenseInfoResponse,
  getOnboardingAssociationInfoResponse,
  getOnboardingOfficeInfoResponse,
  getOnboardingDbaInfoResponse,
  getOnboardingAssociateInviteInfoResponse,
  getOnboardingReferralInfoResponse,
}) => {
  const [userRole, setUserRole] = React.useState(null);

  useEffect(() => {
    getOnboardingPersonalInfo({
      agentId: getAgentIdFromUrl(),
      step: "personal_info",
    });
    getOnboardingLicenseInfo({
      agentId: getAgentIdFromUrl(),
      step: "license_info",
    });
    getOnboardingAssociationInfo({
      agentId: getAgentIdFromUrl(),
      step: "association_info",
    });
  
    getOnboardingOfficeInfo({
      agentId: getAgentIdFromUrl(),
      step: "office_info",
    });
    getOnboardingDbaInfo({ agentId: getAgentIdFromUrl(), step: "dba_info" });
    getOnboardingAgentLicenseStateInfo({
      agentId: getAgentIdFromUrl(),
      step: "agent_license_state",
    });
 

    // New API call to get user role with base URL and agent_id as query parameter
    const fetchUserRole = async () => {
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL;
        const agentId = getAgentIdFromUrl();

        const response = await fetch(
          `${baseUrl}/v1.0/get-user-role?agent_id=${agentId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "internal-id": process.env.REACT_APP_SECRET_ID,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setUserRole(data?.response); // Set the user role in state
        } else {
          console.error(
            "Failed to fetch user role:",
            response.status,
            response.statusText
          );
          const errorText = await response.text();
          console.error("Error response:", errorText);
        }
      } catch (error) {
        console.error("Error fetching user role:", error);
      }
    };

    fetchUserRole();
  }, []);

  // Check if sections have data
  const hasPersonalInfo = getOnboardingPersonalInfoResponse?.data;
  const hasLicenseInfo = getOnboardingLicenseInfoResponse?.data;
  const hasBusinessInfo =
    getOnboardingAssociationInfoResponse?.data ||
    getOnboardingOfficeInfoResponse?.data ||
    getOnboardingDbaInfoResponse?.data;
  // const hasAssociatesInfo = !getOnboardingAssociateInviteInfoResponse?.isFetching;
  // const hasReferralsInfo = !getOnboardingReferralInfoResponse?.isFetching;

  const { data: personalInfoData } = getOnboardingPersonalInfoResponse || {};
  const agentName = personalInfoData
    ? `${personalInfoData.firstname || ""} ${personalInfoData.lastname ||
        ""}`.trim()
    : "";

  const isLeadOrCoLead =
    userRole === "co_team_lead" || userRole === "team_lead" ? true : false;

  return (
    <PageContainer>
      {hasPersonalInfo && (
        <Section>
          <PersonalInfoSection />
        </Section>
      )}

      {hasLicenseInfo && (
        <Section>
          <LicenseSection />
        </Section>
      )}

      {hasBusinessInfo && (
        <Section>
          <BusinessInfoSection agentRole={userRole} />
        </Section>
      )}

      {(isLeadOrCoLead && hasLicenseInfo && hasBusinessInfo && hasPersonalInfo) && (
        <Section>
          <BrandingSection agentName={agentName} />
        </Section>
      )}
      <Section>
        <DocumentDetails />
      </Section>

      {/* {isLeadOrCoLead && hasAssociatesInfo && (
        <Section>
          <AssociatesSection />
        </Section>
      )}

      {hasReferralsInfo && (
        <Section>
          <ReferralsSection />
        </Section>
      )} */}
    </PageContainer>
  );
};

export default container(Onboarding);
